import Phaser from '../lib/phaser.js';
import { DIRECTION } from '../common/direction.js';
import { TEXT_SPEED } from '../config.js';
import { TEXT_SPEED_OPTIONS, BATTLE_SCENE_OPTIONS, BATTLE_STYLE_OPTIONS, SOUND_OPTIONS } from '../common/options.js';
import { exhaustiveGuard } from './guard.js';
import { DataUtils } from './data-utils.js';
import { GAME_FLAG } from '../types/typedef.js';

const LOCAL_STORAGE_KEY = 'MONSTER_TAMER_DATA';

/**
 * @typedef PlayerLocation
 * @type {object}
 * @property {string} area
 * @property {boolean} isInterior
 */

/**
 * @typedef MonsterData
 * @type {object}
 * @property {import('../types/typedef.js').Monster[]} inParty
 */

/**
 * @typedef GlobalState
 * @type {object}
 * @property {object} player
 * @property {object} player.position
 * @property {number} player.position.x
 * @property {number} player.position.y
 * @property {PlayerLocation} player.location
 * @property {import('../common/direction.js').Direction} player.direction
 * @property {string} [player.skin] selected overworld skin asset key
 * @property {string} [player.name] account display name (synced from the api profile)
 * @property {string} [player.avatar] PFP id (a PLAYER_SKIN_* key) chosen for the account
 * @property {string} [player.bio] short account bio
 * @property {object} options
 * @property {import('../common/options.js').TextSpeedMenuOptions} options.textSpeed
 * @property {import('../common/options.js').BattleSceneMenuOptions} options.battleSceneAnimations
 * @property {import('../common/options.js').BattleStyleMenuOptions} options.battleStyle
 * @property {import('../common/options.js').SoundMenuOptions} options.sound
 * @property {import('../common/options.js').SoundMenuOptions} [options.music] background-music toggle (defaults ON)
 * @property {import('../common/options.js').SoundMenuOptions} [options.sfx] sound-effects toggle (defaults ON)
 * @property {import('../common/options.js').VolumeMenuOptions} options.volume
 * @property {import('../common/options.js').MenuColorOptions} options.menuColor
 * @property {boolean} gameStarted
 * @property {number} [gold] off-chain Gold balance (Kintara economy; $TOKEN is on-chain only)
 * @property {MonsterData} monsters
 * @property {import('../types/typedef.js').Inventory} inventory
 * @property {number[]} itemsPickedUp
 * @property {number[]} viewedEvents
 * @property {import('../types/typedef.js').GameFlag[]} flags
 * @property {string[]} defeatedNpcs
 * @property {ProgressState} [progress] onboarding objective progress (counters + step index)
 * @property {boolean} [howToPlaySeen] whether the first-run "How to Play" overlay has been dismissed
 */

/**
 * Lightweight onboarding-objective progress. Drives the on-screen objective tracker so the
 * world has a visible goal. Counters advance on the relevant gameplay events; `step` is the
 * index into the objective chain (see OBJECTIVE_CHAIN in the world HUD).
 * @typedef ProgressState
 * @type {object}
 * @property {number} step current objective index in the chain
 * @property {number} battlesWon total battles won
 * @property {number} monstersCaught total monsters caught
 * @property {number} cardsOrdered real graded cards ordered from the Card Shop
 */

/** @type {GlobalState} */
const initialState = {
  player: {
    position: {
      x: 0,
      y: 0,
    },
    direction: DIRECTION.DOWN,
    location: {
      // New players land in the big generated open-world (see scripts/gen-world.mjs).
      area: 'overworld_1',
      isInterior: false,
    },
    // Selected overworld skin (set in profile setup). Defaults to the first MPWSP skin.
    skin: 'PLAYER_SKIN_1',
    // Account profile (synced from the api). Empty until the player sets it up.
    name: '',
    avatar: 'PLAYER_SKIN_1',
    bio: '',
  },
  options: {
    textSpeed: TEXT_SPEED_OPTIONS.MID,
    battleSceneAnimations: BATTLE_SCENE_OPTIONS.ON,
    battleStyle: BATTLE_STYLE_OPTIONS.SHIFT,
    sound: SOUND_OPTIONS.ON,
    music: SOUND_OPTIONS.ON,
    sfx: SOUND_OPTIONS.ON,
    volume: 2, // default ~50% (was 4 = full) — music was too loud on first load
    menuColor: 0,
  },
  gameStarted: false,
  // Off-chain Gold balance (Kintara economy). Starter grant = GAME.startingGold (100).
  // $TOKEN is never stored here — it is the on-chain wallet balance (see economy.md).
  gold: 100,
  monsters: {
    inParty: [],
    inStorage: [], // PC box: caught beasts beyond the 6-slot party live here
  },
  inventory: [
    {
      item: {
        id: 1,
      },
      quantity: 10,
    },
    {
      item: {
        id: 2,
      },
      quantity: 5,
    },
  ],
  itemsPickedUp: [],
  viewedEvents: [],
  flags: [],
  defeatedNpcs: [],
  progress: {
    step: 0,
    battlesWon: 0,
    monstersCaught: 0,
    cardsOrdered: 0,
  },
  // First-run "How to Play" onboarding overlay. false = show it once on first world entry.
  howToPlaySeen: false,
};

export const DATA_MANAGER_STORE_KEYS = Object.freeze({
  PLAYER_POSITION: 'PLAYER_POSITION',
  PLAYER_DIRECTION: 'PLAYER_DIRECTION',
  PLAYER_LOCATION: 'PLAYER_LOCATION',
  PLAYER_SKIN: 'PLAYER_SKIN',
  PLAYER_NAME: 'PLAYER_NAME',
  PLAYER_AVATAR: 'PLAYER_AVATAR',
  PLAYER_BIO: 'PLAYER_BIO',
  PLAYER_XP: 'PLAYER_XP',
  OPTIONS_TEXT_SPEED: 'OPTIONS_TEXT_SPEED',
  OPTIONS_BATTLE_SCENE_ANIMATIONS: 'OPTIONS_BATTLE_SCENE_ANIMATIONS',
  OPTIONS_BATTLE_STYLE: 'OPTIONS_BATTLE_STYLE',
  OPTIONS_SOUND: 'OPTIONS_SOUND',
  OPTIONS_MUSIC: 'OPTIONS_MUSIC',
  OPTIONS_SFX: 'OPTIONS_SFX',
  OPTIONS_VOLUME: 'OPTIONS_VOLUME',
  OPTIONS_MENU_COLOR: 'OPTIONS_MENU_COLOR',
  GAME_STARTED: 'GAME_STARTED',
  GOLD: 'GOLD',
  MONSTERS_IN_PARTY: 'MONSTERS_IN_PARTY',
  MONSTERS_IN_STORAGE: 'MONSTERS_IN_STORAGE',
  INVENTORY: 'INVENTORY',
  ITEMS_PICKED_UP: 'ITEMS_PICKED_UP',
  VIEWED_EVENTS: 'VIEWED_EVENTS',
  FLAGS: 'FLAGS',
  DEFEATED_NPCS: 'DEFEATED_NPCS',
  PROGRESS: 'PROGRESS',
  HOW_TO_PLAY_SEEN: 'HOW_TO_PLAY_SEEN',
});

class DataManager extends Phaser.Events.EventEmitter {
  /** @type {Phaser.Data.DataManager} */
  #store;

  constructor() {
    super();
    this.#store = new Phaser.Data.DataManager(this);
    // initialize state with initial values
    this.#updateDataManger(initialState);
  }

  /** @type {Phaser.Data.DataManager} */
  get store() {
    return this.#store;
  }

  /**
   * @returns {void}
   */
  loadData() {
    // attempt to load data from browser storage and populate the data manager
    if (typeof Storage === 'undefined') {
      console.warn(
        `[${DataManager.name}:loadData] localStorage is not supported, will not be able to save and load data.`
      );
      return;
    }

    const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedData === null) {
      return;
    }
    try {
      // TODO: we should add error handling and data validation at this step to make sure we get the data we expect.
      /** @type {GlobalState} */
      const parsedData = JSON.parse(savedData);
      // update the state with the saved data
      this.#updateDataManger(parsedData);
    } catch (error) {
      console.warn(
        `[${DataManager.name}:loadData] encountered an error while attempting to load and parse saved data.`
      );
    }
  }

  /**
   * @returns {void}
   */
  saveData() {
    // attempt to storage data in browser storage from data manager
    if (typeof Storage === 'undefined') {
      console.warn(
        `[${DataManager.name}:saveData] localStorage is not supported, will not be able to save and load data.`
      );
      return;
    }
    const dataToSave = this.#dataManagerDataToGlobalStateObject();
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
  }

  /**
   * @returns {void}
   */
  startNewGame() {
    // get existing data before resetting all of the data, so we can persist options data
    const existingData = { ...this.#dataManagerDataToGlobalStateObject() };
    existingData.player.position = { ...initialState.player.position };
    existingData.player.location = { ...initialState.player.location };
    existingData.player.direction = initialState.player.direction;
    existingData.player.location = { ...initialState.player.location };
    existingData.gameStarted = initialState.gameStarted;
    existingData.monsters = {
      inParty: [...initialState.monsters.inParty],
    };
    // Deep-copy (was a bare reference assignment, unlike every sibling below): without this, a new
    // game shares — and then mutates — the module-level initialState.inventory, leaking item state
    // into the NEXT new game / guest session.
    existingData.inventory = JSON.parse(JSON.stringify(initialState.inventory));
    existingData.itemsPickedUp = [...initialState.itemsPickedUp];
    existingData.viewedEvents = [...initialState.viewedEvents];
    existingData.flags = [...initialState.flags];
    existingData.defeatedNpcs = [...initialState.defeatedNpcs];
    existingData.progress = { ...initialState.progress };

    this.#store.reset();
    this.#updateDataManger(existingData);
    this.saveData();
  }

  /**
   * @returns {number}
   */
  getAnimatedTextSpeed() {
    /** @type {import('../common/options.js').TextSpeedMenuOptions | undefined} */
    const chosenTextSpeed = this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_TEXT_SPEED);
    if (chosenTextSpeed === undefined) {
      return TEXT_SPEED.MEDIUM;
    }

    switch (chosenTextSpeed) {
      case TEXT_SPEED_OPTIONS.FAST:
        return TEXT_SPEED.FAST;
      case TEXT_SPEED_OPTIONS.MID:
        return TEXT_SPEED.MEDIUM;
      case TEXT_SPEED_OPTIONS.SLOW:
        return TEXT_SPEED.SLOW;
      default:
        exhaustiveGuard(chosenTextSpeed);
    }
  }

  /**
   * @param {Phaser.Scene} scene
   * @returns {import('../types/typedef.js').InventoryItem[]}
   */
  getInventory(scene) {
    /** @type {import('../types/typedef.js').InventoryItem[]} */
    const items = [];
    /** @type {import('../types/typedef.js').Inventory} */
    const inventory = this.#store.get(DATA_MANAGER_STORE_KEYS.INVENTORY);
    inventory.forEach((baseItem) => {
      const item = DataUtils.getItem(scene, baseItem.item.id);
      items.push({
        item: item,
        quantity: baseItem.quantity,
      });
    });
    return items;
  }

  /**
   * @param {import('../types/typedef.js').InventoryItem[]} items
   * @returns {void}
   */
  updateInventory(items) {
    /** @type {import('../types/typedef.js').BaseInventoryItem[]} */
    const inventory = items.map((item) => {
      return {
        item: {
          id: item.item.id,
        },
        quantity: item.quantity,
      };
    });
    this.#store.set(DATA_MANAGER_STORE_KEYS.INVENTORY, inventory);
  }

  /**
   * @param {import('../types/typedef.js').Item} item
   * @param {number} quantity
   * @returns {void}
   */
  addItem(item, quantity) {
    /** @type {import('../types/typedef.js').Inventory} */
    const inventory = this.#store.get(DATA_MANAGER_STORE_KEYS.INVENTORY);
    const existingItem = inventory.find((inventoryItem) => {
      return inventoryItem.item.id === item.id;
    });
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      inventory.push({
        item,
        quantity,
      });
    }
    this.#store.set(DATA_MANAGER_STORE_KEYS.INVENTORY, inventory);
    this.saveData();
  }

  /**
   * @param {number} itemId
   * @returns {void}
   */
  addItemPickedUp(itemId) {
    /** @type {number[]} */
    const itemsPickedUp = this.#store.get(DATA_MANAGER_STORE_KEYS.ITEMS_PICKED_UP) || [];
    itemsPickedUp.push(itemId);
    this.#store.set(DATA_MANAGER_STORE_KEYS.ITEMS_PICKED_UP, itemsPickedUp);
  }

  /**
   * @returns {boolean}
   */
  isPartyFull() {
    const partySize = this.#store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY).length;
    return partySize === 6;
  }

  /**
   * Adds the provided eventId to the viewed events in the data manager so player does
   * not see the event again.
   * @param {number} eventId
   * @returns {void}
   */
  viewedEvent(eventId) {
    /** @type {Set<number>} */
    const viewedEvents = new Set(this.#store.get(DATA_MANAGER_STORE_KEYS.VIEWED_EVENTS) || []);
    viewedEvents.add(eventId);
    this.#store.set(DATA_MANAGER_STORE_KEYS.VIEWED_EVENTS, Array.from(viewedEvents));
  }

  /**
   * @returns {Set<string>}
   */
  getFlags() {
    return new Set(this.#store.get(DATA_MANAGER_STORE_KEYS.FLAGS) || []);
  }

  /**
   * @param {GAME_FLAG} flag
   * @returns {void}
   */
  addFlag(flag) {
    /** @type {Set<string>} */
    const existingFlags = new Set(this.#store.get(DATA_MANAGER_STORE_KEYS.FLAGS) || []);
    existingFlags.add(flag);
    this.#store.set(DATA_MANAGER_STORE_KEYS.FLAGS, Array.from(existingFlags));
  }

  /**
   * @param {GAME_FLAG} flag
   * @returns {void}
   */
  removeFlag(flag) {
    /** @type {Set<string>} */
    const existingFlags = new Set(this.#store.get(DATA_MANAGER_STORE_KEYS.FLAGS) || []);
    existingFlags.delete(flag);
    this.#store.set(DATA_MANAGER_STORE_KEYS.FLAGS, Array.from(existingFlags));
  }

  /**
   * Adds the provided npcId to the defeated npc set in the data manager so player does
   * not battle that npc again.
   * @param {number} npcId
   * @returns {void}
   */
  addDefeatedNpc(npcId) {
    /** @type {Set<number>} */
    const defeatedNpcs = this.#store.get(DATA_MANAGER_STORE_KEYS.DEFEATED_NPCS);
    defeatedNpcs.add(npcId);
    this.saveData();
  }

  /**
   * @returns {Set<number>}
   */
  getDefeatedNpcs() {
    return this.#store.get(DATA_MANAGER_STORE_KEYS.DEFEATED_NPCS);
  }

  /**
   * Current off-chain Gold balance. Source of truth is the api (`players.gold`) when the
   * player is connected; this local value mirrors it and keeps single-player working offline.
   * @returns {number}
   */
  getGold() {
    const gold = this.#store.get(DATA_MANAGER_STORE_KEYS.GOLD);
    return typeof gold === 'number' ? gold : initialState.gold;
  }

  /**
   * Set the local Gold balance (e.g. after a quest claim or syncing from the api `/player`).
   * @param {number} amount
   * @returns {void}
   */
  setGold(amount) {
    this.#store.set(DATA_MANAGER_STORE_KEYS.GOLD, Math.max(0, Math.round(amount)));
  }

  /**
   * Onboarding-objective progress (counters + current step). Always returns a fully-populated
   * object so callers/HUD never deal with undefined fields.
   * @returns {ProgressState}
   */
  getProgress() {
    return { ...initialState.progress, ...(this.#store.get(DATA_MANAGER_STORE_KEYS.PROGRESS) || {}) };
  }

  /**
   * Merge a partial update into the progress state and persist. Setting via the store also
   * fires a `changedata-PROGRESS` event the world HUD listens to for live updates.
   * @param {Partial<ProgressState>} partial
   * @returns {void}
   */
  setProgress(partial) {
    this.#store.set(DATA_MANAGER_STORE_KEYS.PROGRESS, { ...this.getProgress(), ...partial });
  }

  /** Increment the battles-won counter (drives the objective tracker). @returns {void} */
  recordBattleWon() {
    this.setProgress({ battlesWon: this.getProgress().battlesWon + 1 });
    this.saveData();
  }

  /** Increment the monsters-caught counter (drives the objective tracker). @returns {void} */
  recordMonsterCaught() {
    this.setProgress({ monstersCaught: this.getProgress().monstersCaught + 1 });
    this.saveData();
  }

  /** Increment the cards-ordered counter (drives the objective tracker). @returns {void} */
  recordCardOrdered() {
    this.setProgress({ cardsOrdered: this.getProgress().cardsOrdered + 1 });
  }

  /**
   * @param {GlobalState} data
   * @returns {void}
   */
  #updateDataManger(data) {
    this.#store.set({
      [DATA_MANAGER_STORE_KEYS.PLAYER_POSITION]: data.player.position,
      [DATA_MANAGER_STORE_KEYS.PLAYER_DIRECTION]: data.player.direction,
      [DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION]: data.player.location || { ...initialState.player.location },
      [DATA_MANAGER_STORE_KEYS.PLAYER_SKIN]: data.player.skin || initialState.player.skin,
      [DATA_MANAGER_STORE_KEYS.PLAYER_NAME]: data.player.name ?? initialState.player.name,
      [DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR]: data.player.avatar || initialState.player.avatar,
      [DATA_MANAGER_STORE_KEYS.PLAYER_BIO]: data.player.bio ?? initialState.player.bio,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_TEXT_SPEED]: data.options.textSpeed,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_BATTLE_SCENE_ANIMATIONS]: data.options.battleSceneAnimations,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_BATTLE_STYLE]: data.options.battleStyle,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_SOUND]: data.options.sound,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC]: data.options.music ?? initialState.options.music,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_SFX]: data.options.sfx ?? initialState.options.sfx,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME]: data.options.volume,
      [DATA_MANAGER_STORE_KEYS.OPTIONS_MENU_COLOR]: data.options.menuColor,
      [DATA_MANAGER_STORE_KEYS.GAME_STARTED]: data.gameStarted,
      [DATA_MANAGER_STORE_KEYS.GOLD]: typeof data.gold === 'number' ? data.gold : initialState.gold,
      [DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY]: data.monsters.inParty,
      [DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE]: data.monsters.inStorage || [],
      [DATA_MANAGER_STORE_KEYS.INVENTORY]: data.inventory,
      [DATA_MANAGER_STORE_KEYS.ITEMS_PICKED_UP]: data.itemsPickedUp || [...initialState.itemsPickedUp],
      [DATA_MANAGER_STORE_KEYS.VIEWED_EVENTS]: data.viewedEvents || [...initialState.viewedEvents],
      [DATA_MANAGER_STORE_KEYS.FLAGS]: data.flags || [...initialState.flags],
      [DATA_MANAGER_STORE_KEYS.DEFEATED_NPCS]: new Set(data.defeatedNpcs || []),
      [DATA_MANAGER_STORE_KEYS.PROGRESS]: { ...initialState.progress, ...(data.progress || {}) },
      [DATA_MANAGER_STORE_KEYS.HOW_TO_PLAY_SEEN]: data.howToPlaySeen ?? initialState.howToPlaySeen,
    });
  }

  /**
   * @returns {GlobalState}
   */
  #dataManagerDataToGlobalStateObject() {
    return {
      player: {
        position: {
          x: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION).x,
          y: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_POSITION).y,
        },
        direction: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_DIRECTION),
        location: { ...this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_LOCATION) },
        skin: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_SKIN),
        name: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_NAME),
        avatar: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR),
        bio: this.#store.get(DATA_MANAGER_STORE_KEYS.PLAYER_BIO),
      },
      options: {
        textSpeed: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_TEXT_SPEED),
        battleSceneAnimations: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_BATTLE_SCENE_ANIMATIONS),
        battleStyle: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_BATTLE_STYLE),
        sound: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_SOUND),
        music: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC) ?? initialState.options.music,
        sfx: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_SFX) ?? initialState.options.sfx,
        volume: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME),
        menuColor: this.#store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_MENU_COLOR),
      },
      gameStarted: this.#store.get(DATA_MANAGER_STORE_KEYS.GAME_STARTED),
      gold: this.#store.get(DATA_MANAGER_STORE_KEYS.GOLD),
      monsters: {
        inParty: [...this.#store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY)],
        inStorage: [...(this.#store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE) || [])],
      },
      inventory: this.#store.get(DATA_MANAGER_STORE_KEYS.INVENTORY),
      itemsPickedUp: [...(this.#store.get(DATA_MANAGER_STORE_KEYS.ITEMS_PICKED_UP) || [])],
      viewedEvents: [...(this.#store.get(DATA_MANAGER_STORE_KEYS.VIEWED_EVENTS) || [])],
      flags: [...(this.#store.get(DATA_MANAGER_STORE_KEYS.FLAGS) || [])],
      defeatedNpcs: Array.from(this.#store.get(DATA_MANAGER_STORE_KEYS.DEFEATED_NPCS) || new Set()),
      progress: { ...initialState.progress, ...(this.#store.get(DATA_MANAGER_STORE_KEYS.PROGRESS) || {}) },
      howToPlaySeen: this.#store.get(DATA_MANAGER_STORE_KEYS.HOW_TO_PLAY_SEEN) ?? initialState.howToPlaySeen,
    };
  }
}

export const dataManager = new DataManager();
